import { AnimatePresence, motion } from "motion/react";
import styled from "@emotion/styled";
import { useRef } from "react";
import { useAudioCaptureActionsContext } from "./audio-capture-actions.context";
import {
  AudioCaptureHeader,
  AudioCaptureModals,
  AudioCaptureOnboarding,
  AudioCaptureToolbar,
  AudioCaptureTranscript,
} from "./components";

export function AudioCaptureActionsContent() {
  const { recording, ui, environment, refs, actions } = useAudioCaptureActionsContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const audioRecordingFailureMessage = (() => {
    if (recording.isProcessing || recording.isUploading) {
      return null;
    }

    if (recording.uploadStatus.state === "error") {
      return recording.uploadStatus.errorMessage ?? "Upload failed";
    }

    if (recording.transformStatus.state === "error") {
      return recording.transformStatus.errorMessage ?? "Processing failed";
    }

    return null;
  })();

  return (
    <>
      <AudioCaptureModals
        isAutoStopModalVisible={ui.isAutoStopModalVisible}
        onAutoStopClose={() => actions.autoStop.setVisible(false)}
        isDeleteModalOpen={ui.showDeleteModal}
        onDeleteClose={actions.deleteDialog.close}
        onDeleteConfirm={actions.deleteDialog.confirm}
      />
      <AnimatePresence>
        {!ui.showDeleteModal && (
          <StyledContainerWrapper>
            <AudioCaptureOnboarding show={ui.showOnboardingTooltip} triggerRef={containerRef} />
            <StyledContainer
              ref={containerRef}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4, transition: { duration: 0.08, ease: [0.4, 0.0, 0.2, 1] } }}
              transition={{ delay: 0.15, duration: 0.08, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <StyledHeader>
                <AudioCaptureHeader
                  status={recording.status}
                  activeMicrophoneLabel={recording.activeMicrophoneLabel}
                  isDesktop={environment.isDesktop}
                  dropdownItems={ui.dropdownItems}
                  shouldShowDropdown={ui.shouldShowDropdown}
                  onDelete={() => {
                    void actions.deleteDialog.open();
                  }}
                />
              </StyledHeader>
              <AudioCaptureTranscript
                transcript={recording.transcript}
                isAdmin={environment.isAdmin}
                isTextTranscript={ui.isTextTranscript}
                onToggleAdminMode={actions.transcript.toggleAdminMode}
                transcriptContainerRef={refs.transcriptContainerRef}
                onTranscriptScroll={actions.transcript.handleScroll}
                isMicrophoneDisconnected={recording.microphone.isDisconnected}
                isAudioSilent={recording.microphone.isAudioSilent}
                status={recording.status}
                shouldShowDesktopDownloadNotice={ui.shouldShowDesktopDownloadNotice}
                onDownloadClick={actions.download.openDesktopLink}
                onOpenMicrophoneSystemPermissions={
                  actions.systemPermissions.onOpenMicrophoneSystemPermissions
                }
                audioRecordingFailureMessage={audioRecordingFailureMessage}
              />
              <AudioCaptureToolbar
                recording={recording}
                isMicrophoneDisconnected={recording.microphone.isDisconnected}
                onPause={actions.recording.pause}
                onResume={actions.recording.resume}
                onStart={() => actions.recording.start()}
                onClear={actions.recording.clear}
                onFinish={actions.recording.finish}
                onRetryUpload={actions.recording.retryUpload}
                onRetryTransform={actions.recording.retryTransform}
                uploadStatus={recording.uploadStatus}
                transformStatus={recording.transformStatus}
              />
            </StyledContainer>
          </StyledContainerWrapper>
        )}
      </AnimatePresence>
    </>
  );
}

const StyledContainerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    align-items: flex-start;
  }
`;

const StyledContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[4]};
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  width: 415px;

  /* For phones */
  max-width: calc(100vw - 32px);
`;

const StyledHeader = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  justify-content: space-between;
  width: 100%;
`;
